import { useState, useCallback, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    MagnifyingGlassIcon,
    BookOpenIcon,
    AcademicCapIcon,
    CurrencyRupeeIcon,
    ClockIcon,
    CheckBadgeIcon,
    ArrowRightIcon,
    MapPinIcon,
    BuildingOfficeIcon,
    StarIcon,
    UsersIcon,
    TrophyIcon,
    XMarkIcon,
    AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { courseAPI, degreeAPI, specializationAPI, approvalAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import EnquiryModal from '../../components/Common/EnquiryModal'

const Courses = () => {
    const [filters, setFilters] = useState({
        search: '',
        level: '',
        degrees: [],
        specializations: [],
        approvals: [],
        feeRange: '',
        page: 1,
        limit: 12,
        sort: 'order'
    })
    const [searchParams] = useSearchParams()

    // Sync filters with URL params
    useEffect(() => {
        const level = searchParams.get('level') || ''
        const degrees = searchParams.get('degrees') ? searchParams.get('degrees').split(',') : []
        const specializations = searchParams.get('specializations') ? searchParams.get('specializations').split(',') : []
        const approvals = searchParams.get('approvals') ? searchParams.get('approvals').split(',') : []
        const feeRange = searchParams.get('feeRange') || ''
        const search = searchParams.get('search') || ''

        setFilters(prev => ({
            ...prev,
            level,
            degrees,
            specializations,
            approvals,
            feeRange,
            search,
            page: 1
        }))
    }, [searchParams])

    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [enquiryModal, setEnquiryModal] = useState({
        isOpen: false,
        course: null,
        type: ''
    })

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['courses', filters],
        queryFn: () => {
            // Convert arrays to comma-separated strings for API
            const apiFilters = {
                ...filters,
                degrees: filters.degrees.length > 0 ? filters.degrees.join(',') : undefined,
                specializations: filters.specializations.length > 0 ? filters.specializations.join(',') : undefined,
                approvals: filters.approvals.length > 0 ? filters.approvals.join(',') : undefined,
            };
            // Remove empty arrays
            Object.keys(apiFilters).forEach(key => {
                if (apiFilters[key] === undefined || apiFilters[key] === '') {
                    delete apiFilters[key];
                }
            });
            return courseAPI.getCourses(apiFilters);
        },
        keepPreviousData: false,
    })

    // Fetch master data for filters
    const { data: degreesData, isLoading: degreesLoading, error: degreesError } = useQuery({
        queryKey: ['degrees'],
        queryFn: () => degreeAPI.getDegrees()
    })

    // Fetch specializations based on selected degrees
    const { data: specializationsData, isLoading: specializationsLoading, error: specializationsError } = useQuery({
        queryKey: ['specializations', filters.degrees],
        queryFn: () => {
            if (filters.degrees.length > 0) {
                // Fetch specializations for selected degrees
                return Promise.all(
                    filters.degrees.map(degreeId =>
                        specializationAPI.getSpecializations({ degree: degreeId })
                    )
                ).then(results => {
                    // Flatten and deduplicate specializations
                    const allSpecializations = results.flatMap(result => result.data?.specializations || []);
                    const uniqueSpecializations = allSpecializations.filter((spec, index, self) =>
                        index === self.findIndex(s => s._id === spec._id)
                    );
                    return { data: { specializations: uniqueSpecializations } };
                });
            } else {
                // Fetch all specializations when no degrees selected
                return specializationAPI.getSpecializations();
            }
        },
        enabled: true
    })

    const { data: approvalsData, isLoading: approvalsLoading, error: approvalsError } = useQuery({
        queryKey: ['approvals'],
        queryFn: () => approvalAPI.getApprovals()
    })

    const degrees = (degreesData?.data?.degrees || []).filter(degree =>
        !filters.level || degree.level === filters.level
    )
    const specializations = specializationsData?.data?.specializations || []
    const approvals = approvalsData?.data?.approvals || []

    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                [key]: value,
                page: 1 // Reset to first page when filters change
            };

            // If level changes, reset degrees and specializations
            if (key === 'level') {
                newFilters.degrees = [];
                newFilters.specializations = [];
            }

            return newFilters;
        })
    }

    const handleMultiSelectFilter = (key, value, checked) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                [key]: checked
                    ? [...prev[key], value]
                    : prev[key].filter(item => item !== value),
                page: 1
            };

            // If degrees are being changed, clear specializations
            if (key === 'degrees') {
                newFilters.specializations = [];
            }

            return newFilters;
        })
    }

    const clearAllFilters = () => {
        setFilters({
            search: '',
            level: '',
            degrees: [],
            specializations: [],
            approvals: [],
            feeRange: '',
            page: 1,
            limit: 12,
            sort: 'order'
        })
    }

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }))
    }

    const handleEnquiry = (course, type) => {
        setEnquiryModal({
            isOpen: true,
            course,
            type
        })
    }

    const handleCloseEnquiryModal = () => {
        setEnquiryModal({
            isOpen: false,
            course: null,
            type: ''
        })
    }

    // Course Image Component removed as per new imageless design

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load courses" onRetry={refetch} />
            </div>
        )
    }

    const courses = data?.data?.courses || []
    const pagination = data?.data?.pagination || {}

    return (
        <>
            <Helmet>
                <title>Courses & Programs | Margdarshak</title>
                <meta name="description" content="Browse top online and distance learning courses from UGC recognized universities. Find MBA, BCA, MCA, and other programs with flexible learning options." />
                <meta name="keywords" content="online courses, distance learning, MBA, BCA, MCA, UGC recognized, flexible learning, higher education" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 pt-20">
                {/* Modern Hero Section */}
                <section className="relative overflow-hidden bg-white py-16 lg:py-24 border-b border-gray-100">
                    {/* Subtle Grid Pattern */}
                    <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-primary-50 rounded-full blur-3xl opacity-50"></div>
                    <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-72 h-72 bg-accent-50 rounded-full blur-3xl opacity-50"></div>

                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="max-w-4xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="inline-flex items-center space-x-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-widest mb-8 border border-primary-100"
                            >
                                <AcademicCapIcon className="w-5 h-5" />
                                <span>Discover Programs</span>
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight tracking-tight"
                            >
                                Top <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-accent-600">Courses</span> &{' '}
                                <span className="text-primary-600">Programs</span>
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed"
                            >
                                Discover India's leading online and distance learning programs from UGC recognized universities, tailored for your career growth.
                            </motion.p>
                        </div>
                    </div>
                </section>

                {/* Search and Sort Section */}
                <section className="py-6 bg-white border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col lg:flex-row gap-4 items-center">
                            {/* Mobile Filter Toggle */}
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="lg:hidden flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                <AdjustmentsHorizontalIcon className="w-5 h-5" />
                                <span>Filters</span>
                            </button>

                            {/* Search */}
                            <div className="flex-1">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search courses..."
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                                    />
                                </div>
                            </div>

                            {/* Sort - Removed as per user request */}
                        </div>
                    </div>
                </section>

                {/* Results Section */}
                <section className="py-12">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col lg:flex-row gap-8">
                            {/* Sidebar Filters */}
                            <div className={`lg:w-80 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}>
                                <div className="bg-white rounded-xl shadow-soft p-6 sticky top-24">
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={clearAllFilters}
                                                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                                            >
                                                Clear All
                                            </button>
                                            <button
                                                onClick={() => setSidebarOpen(false)}
                                                className="lg:hidden p-1 hover:bg-gray-100 rounded"
                                            >
                                                <XMarkIcon className="w-5 h-5 text-gray-500" />
                                            </button>
                                        </div>
                                    </div>





                                    {/* Level Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Level</label>
                                        <select
                                            value={filters.level}
                                            onChange={(e) => handleFilterChange('level', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        >
                                            <option value="">All Levels</option>
                                            <option value="Undergraduate (UG)">Undergraduate</option>
                                            <option value="Postgraduate (PG)">Postgraduate</option>
                                            <option value="Diploma">Diploma</option>
                                            <option value="Certificate">Certificate</option>
                                        </select>
                                    </div>

                                    {/* Degrees Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Degrees</label>
                                        {degreesLoading ? (
                                            <div className="flex items-center justify-center py-4">
                                                <LoadingSpinner size="sm" />
                                            </div>
                                        ) : degreesError ? (
                                            <div className="text-red-500 text-sm">Failed to load degrees</div>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto space-y-2">
                                                {degrees.map((degree) => (
                                                    <label key={degree._id} className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.degrees.includes(degree._id)}
                                                            onChange={(e) => handleMultiSelectFilter('degrees', degree._id, e.target.checked)}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{degree.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Specializations Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Specializations</label>
                                        {specializationsLoading ? (
                                            <div className="flex items-center justify-center py-4">
                                                <LoadingSpinner size="sm" />
                                            </div>
                                        ) : specializationsError ? (
                                            <div className="text-red-500 text-sm">Failed to load specializations</div>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto space-y-2">
                                                {specializations.map((specialization) => (
                                                    <label key={specialization._id} className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.specializations.includes(specialization._id)}
                                                            onChange={(e) => handleMultiSelectFilter('specializations', specialization._id, e.target.checked)}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{specialization.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Approvals Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Approvals</label>
                                        {approvalsLoading ? (
                                            <div className="flex items-center justify-center py-4">
                                                <LoadingSpinner size="sm" />
                                            </div>
                                        ) : approvalsError ? (
                                            <div className="text-red-500 text-sm">Failed to load approvals</div>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto space-y-2">
                                                {approvals.map((approval) => (
                                                    <label key={approval._id} className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.approvals.includes(approval._id)}
                                                            onChange={(e) => handleMultiSelectFilter('approvals', approval._id, e.target.checked)}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{approval.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Fee Range Filter */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-3">Fee Range</label>
                                        <select
                                            value={filters.feeRange}
                                            onChange={(e) => handleFilterChange('feeRange', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        >
                                            <option value="">All Fee Ranges</option>
                                            <option value="below_2_lakhs">Below ₹2 Lakhs</option>
                                            <option value="above_2_lakhs">Above ₹2 Lakhs</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Main Content */}
                            <div className="flex-1">
                                {isLoading ? (
                                    <div className="flex justify-center py-12">
                                        <LoadingSpinner size="lg" />
                                    </div>
                                ) : (
                                    <>
                                        {/* Results Count */}
                                        <div className="mb-8">
                                            {courses.length > 0 ? (
                                                <p className="text-gray-600">
                                                    Showing {courses.length} of {pagination.total_count || 0} courses
                                                </p>
                                            ) : (
                                                <div className="text-center py-12">
                                                    <div className="text-gray-500 mb-4">
                                                        <BookOpenIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
                                                        <p className="text-gray-600 mb-4">
                                                            Try adjusting your filters or search terms to find more results.
                                                        </p>
                                                        <button
                                                            onClick={clearAllFilters}
                                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                                        >
                                                            Clear All Filters
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Courses Grid */}
                                        {courses.length > 0 && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                                                {courses.map((course, index) => (
                                                    <motion.div
                                                        key={course._id}
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                                        className="bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 hover:border-primary-100 transition-all duration-300 overflow-hidden flex flex-col h-full group relative"
                                                    >
                                                        {/* Top accent line */}
                                                        <div className="h-1.5 w-full bg-gradient-to-r from-primary-500 to-accent-500"></div>

                                                        <div className="p-6 flex flex-col flex-1">
                                                            {/* Badges / Top Info */}
                                                            <div className="flex justify-between items-start mb-4">
                                                                <div className="flex flex-wrap gap-2">
                                                                    {course.level && (
                                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100">
                                                                            <AcademicCapIcon className="w-3.5 h-3.5 mr-1" />
                                                                            {course.level}
                                                                        </span>
                                                                    )}
                                                                    {course.duration && (
                                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                                                                            <ClockIcon className="w-3.5 h-3.5 mr-1" />
                                                                            {course.duration}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {course.featured && (
                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-accent-100 text-accent-800 border border-accent-200">
                                                                        Featured
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Title */}
                                                            <div className="mb-4">
                                                                <Link to={`/courses/${course.slug}`}>
                                                                    <h3 className="text-xl font-bold text-gray-900 leading-tight group-hover:text-primary-600 transition-colors duration-200">
                                                                        {course.degreeId ? course.degreeId.name : `${course.level} - ${course.specialization ? course.specialization.join(', ') : 'Course'}`}
                                                                    </h3>
                                                                </Link>
                                                                <div className="flex items-center text-gray-500 mt-2 hover:text-gray-700 transition-colors">
                                                                    <BuildingOfficeIcon className="w-4 h-4 mr-1.5 shrink-0" />
                                                                    <span className="text-sm font-medium line-clamp-1 border-b border-transparent hover:border-gray-300">
                                                                        {course.universityId?.name}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* Summary */}
                                                            {course.short_summary && (
                                                                <p className="text-gray-600 text-sm line-clamp-2 mb-5 leading-relaxed">
                                                                    {course.short_summary}
                                                                </p>
                                                            )}

                                                            {/* Specializations (if available) */}
                                                            {course.specialization && course.specialization.filter(spec => !/^[0-9a-fA-F]{24}$/.test(spec)).length > 0 && (
                                                                <div className="mb-6">
                                                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Specializations</p>
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {course.specialization.map((spec, idx) => {
                                                                            const isObjectId = /^[0-9a-fA-F]{24}$/.test(spec);
                                                                            if (isObjectId) return null;
                                                                            return (
                                                                                <span key={idx} className="inline-flex items-center px-2 py-1 rounded bg-gray-50 text-gray-600 text-[11px] font-medium border border-gray-100">
                                                                                    {spec}
                                                                                </span>
                                                                            );
                                                                        }).filter(Boolean).slice(0, 3)}
                                                                        {course.specialization.filter(spec => !/^[0-9a-fA-F]{24}$/.test(spec)).length > 3 && (
                                                                            <span className="inline-flex items-center px-2 py-1 rounded bg-gray-50 text-gray-500 text-[11px] font-medium border border-gray-100">
                                                                                +{course.specialization.filter(spec => !/^[0-9a-fA-F]{24}$/.test(spec)).length - 3} more
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}



                                                            {/* Fee & Action Block */}
                                                            <div className="flex flex-col gap-4 mt-auto">
                                                                {/* Fee */}
                                                                <div className="flex justify-between items-center bg-gray-50 px-4 py-3 rounded-xl border border-gray-100">
                                                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg. Fee Type</span>
                                                                    <div className="flex items-center text-gray-900 font-bold">
                                                                        <CurrencyRupeeIcon className="w-4 h-4 mr-1 text-gray-500" />
                                                                        <span>
                                                                            {course.universityId?.fee_range?.type === 'below_2_lakhs' ? 'Below 2 Lakhs' :
                                                                                course.universityId?.fee_range?.type === 'above_2_lakhs' ? 'Above 2 Lakhs' :
                                                                                    course.universityId?.fee_range?.type || 'Contact for Details'}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Action Buttons */}
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <button
                                                                        onClick={() => handleEnquiry(course, 'enquiry')}
                                                                        className="w-full bg-primary-600 text-white px-4 py-2.5 rounded-xl hover:bg-primary-700 transition-all font-semibold text-sm shadow-sm active:scale-[0.98]"
                                                                    >
                                                                        Enquiry Now
                                                                    </button>
                                                                    <div className="flex gap-2">
                                                                        <button
                                                                            onClick={() => handleEnquiry(course, 'brochure')}
                                                                            className="flex-1 bg-white text-gray-700 border border-gray-200 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-all font-semibold text-sm shadow-sm active:scale-[0.98] flex justify-center items-center"
                                                                            title="Download Brochure"
                                                                        >
                                                                            Brochure
                                                                        </button>
                                                                        <a
                                                                            href={`/courses/${course.slug}`}
                                                                            className="flex-shrink-0 bg-primary-50 text-primary-600 border border-primary-100 w-10 py-2.5 rounded-xl hover:bg-primary-100 transition-all font-semibold text-sm shadow-sm active:scale-[0.98] flex justify-center items-center group-hover:bg-primary-600 group-hover:text-white"
                                                                            title="View Details"
                                                                        >
                                                                            <ArrowRightIcon className="w-4 h-4" />
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleEnquiry(course, 'counselling')}
                                                                    className="w-full bg-primary-600 outline-none text-white px-4 py-3 rounded-xl hover:bg-primary-700 transition-all font-bold text-sm shadow-md active:scale-[0.98] flex justify-center items-center"
                                                                >
                                                                    Get Free Career Counselling
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Pagination */}
                                        {pagination.total_pages > 1 && (
                                            <div className="mt-12 flex justify-center">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                                        disabled={pagination.current_page === 1}
                                                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Previous
                                                    </button>

                                                    {Array.from({ length: Math.min(pagination.total_pages, 5) }, (_, i) => {
                                                        const page = i + 1;
                                                        return (
                                                            <button
                                                                key={page}
                                                                onClick={() => handlePageChange(page)}
                                                                className={`px-4 py-2 text-sm font-medium rounded-lg ${page === pagination.current_page
                                                                    ? 'bg-primary-600 text-white'
                                                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                                                    }`}
                                                            >
                                                                {page}
                                                            </button>
                                                        );
                                                    })}

                                                    <button
                                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                                        disabled={pagination.current_page === pagination.total_pages}
                                                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Enquiry Modal */}
            <EnquiryModal
                isOpen={enquiryModal.isOpen}
                onClose={handleCloseEnquiryModal}
                title={
                    enquiryModal.type === 'enquiry' ? 'Quick Enquiry' :
                        enquiryModal.type === 'brochure' ? 'Download Brochure' :
                            'Free Career Counselling'
                }
                source={enquiryModal.type}
                itemName={enquiryModal.course ? `${enquiryModal.course.degreeId ? enquiryModal.course.degreeId.name : `${enquiryModal.course.level} - ${enquiryModal.course.specialization ? enquiryModal.course.specialization.join(', ') : 'Course'}`}` : ''}
                itemType="course"
                itemId={enquiryModal.course?._id || null}
            />
        </>
    )
}

export default Courses
